import { useVirtualizer } from '@tanstack/react-virtual';
import { useLayoutEffect, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Information } from '@mint-lib/icons';
import { Checkbox, Skeleton } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import {
  InfinitePaginatedData,
  TableDataFileHeader,
  TableDataFileValidatedRow,
  type DataFilePreview,
  type DataFilePreviewV1,
  type DataFilePreviewV2,
} from '../../api/types.js';
import {
  DEFAULT_HEADER_CELLS_COUNT,
  FIRST_CELL_WIDTH,
  ROW_HEIGHT,
} from '../../constants/tablesConfig.js';
import {
  useHasSufficientViewportHeight,
  useInfiniteScroll,
} from '../../hooks/index.js';
import {
  EditDataConfigurationFieldVariables,
  useChannelsCatalog,
  useMediaRowNamesById,
  usePlatformsCatalog,
} from '../../queries/index.js';
import { RichTooltip } from '../atoms/RichTooltip.jsx';
import { TableHeadCellSkeleton } from '../atoms/TableHeadCellSkeleton.jsx';
import { AssignChannelDialog } from '../molecules/AssignChannelDialog.jsx';
import { BulkEditConfirmationDialog } from '../molecules/BulkEditConfirmationDialog.jsx';
import { InfinitelyPaginatedTable } from '../molecules/InfinitelyPaginatedTable.jsx';
import { TableBodyValidatedCell } from '../molecules/TableBodyValidatedCell.jsx';
import {
  TableBody,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../molecules/TableComponents.jsx';
import { ValidatedRow } from '../molecules/ValidatedRow.jsx';

type Props = {
  dataFilePreview: DataFilePreview;
  data: InfinitePaginatedData<unknown>;
  headers: TableDataFileHeader[];
  headersMeta: Record<string, number>;
  rows: TableDataFileValidatedRow[];
  selection: {
    isInverted: boolean;
    selectedRows: Record<string, boolean>;
  };
  selectedRowsIds: string[];
  handleSelectAllRows: () => void;
  handleSelectRow: (
    rowID: string,
    rowIndex: number,
  ) => (checked: boolean) => void;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  itemsPerPage: number;
  hasNextPage?: boolean;
  fetchNextPage: () => Promise<unknown>;
  editField: (vars: EditDataConfigurationFieldVariables) => void;
};

export const DataConfigurationTable = ({
  dataFilePreview,
  data,
  headers,
  headersMeta,
  rows,
  selection,
  selectedRowsIds,
  handleSelectAllRows,
  handleSelectRow,
  isFetchingNextPage,
  isLoading,
  itemsPerPage,
  hasNextPage,
  fetchNextPage,
  editField,
}: Props) => {
  const { t } = useTranslation('@myn/import-hub');

  const threshold = (itemsPerPage / 2) * ROW_HEIGHT;

  const scrollableContainer = useInfiniteScroll({
    hasNextPage,
    fetchNextPage,
    threshold,
  });

  // TanStack Virtual returns non-memoizable functions; React Compiler safely skips this component.
  /* oxlint-disable react-hooks-js/incompatible-library */
  // eslint-disable-next-line react-hooks/incompatible-library
  const rowVirtualizer = useVirtualizer({
    getScrollElement: () => scrollableContainer,
    count: rows.length,
    estimateSize: () => ROW_HEIGHT + 1,
    overscan: itemsPerPage,
  });
  /* oxlint-enable react-hooks-js/incompatible-library */

  const virtualRows = rowVirtualizer.getVirtualItems();

  const paddingTop =
    virtualRows.length > 0 ? (virtualRows?.[0]?.start ?? 0) : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() - (virtualRows.at(-1)?.end ?? 0)
      : 0;

  /* Fix HTML table layout */
  const [firstCellRef, setFirstCellRef] = useState<HTMLTableCellElement | null>(
    null,
  );
  const [isFirstCellOverflow, setIsFirstCellOverflow] = useState<
    boolean | null
  >(null);

  useLayoutEffect(() => {
    if (!isLoading && firstCellRef && isFirstCellOverflow === null) {
      setIsFirstCellOverflow(firstCellRef.clientWidth > FIRST_CELL_WIDTH);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, firstCellRef]);
  /*  -------------------  */

  const { platformColumnIndex, channelColumnIndex, mediaRowColumnIndex } =
    headersMeta;

  const [channelToAssign, setChannelToAssign] =
    useState<EditDataConfigurationFieldVariables | null>(null);
  const [bulkEditPayload, setBulkEditPayload] =
    useState<EditDataConfigurationFieldVariables | null>(null);

  const {
    data: { platformsByName },
  } = usePlatformsCatalog();

  const {
    data: { channelsByName },
  } = useChannelsCatalog();

  const { data: mediaRowNamesById } = useMediaRowNamesById(
    {
      campaignCollectionId: (
        dataFilePreview.context as DataFilePreviewV1['context']
      ).journeyId as number,
      activityId: (dataFilePreview.context as DataFilePreviewV2['context'])
        .activityId as string,
    },
    { enabled: dataFilePreview.granularityLevel === 'media_row' },
  );

  const isPlatformLevelGranularity =
    dataFilePreview.granularityLevel === 'platform';

  const handleEditField =
    (rowId: string, rowIndex: number, colIndex: number) =>
    (rawValue: string, displayValue?: string) => {
      const isChannelField = colIndex === channelColumnIndex;
      const isMediaRowField = colIndex === mediaRowColumnIndex;
      const platformCell = rows[rowIndex].validatedCells[platformColumnIndex];
      const channelCell = rows[rowIndex].validatedCells[channelColumnIndex];
      const mediaRowCell = rows[rowIndex].validatedCells[mediaRowColumnIndex];

      const payload = {
        rowId,
        rowIndex,
        colIndex,
        headers: headers,
        value: rawValue,
        displayValue,
        platformValue: platformCell.rawCell,
        channelValue:
          isChannelField || channelCell?.error ? null : channelCell?.rawCell,
        mediaRowValue:
          isMediaRowField || mediaRowCell?.error ? null : mediaRowCell?.rawCell,
      };

      const cellValue = rows[rowIndex].validatedCells[colIndex];

      if (isChannelField && isPlatformLevelGranularity && cellValue.error) {
        setChannelToAssign(payload);
        return;
      }

      if ((isChannelField || isMediaRowField) && cellValue.error) {
        setBulkEditPayload(payload);
        return;
      }
      editField(payload);
    };

  const handleEditChannel = (payload: EditDataConfigurationFieldVariables) => {
    editField(payload);
    setChannelToAssign(null);
  };

  const handleBulkEditChannel = (
    payload: EditDataConfigurationFieldVariables,
  ) => {
    editField({ ...payload, isBulk: true });
    setChannelToAssign(null);
  };

  const getEntitiesDisplayNames = (
    payload: EditDataConfigurationFieldVariables,
  ) => {
    const headerName = headers[payload.colIndex].headerName;
    const displayValue = payload.displayValue as string;
    const platformName = platformsByName[payload.platformValue]?.label;
    const channelName = channelsByName[payload.channelValue as string]?.label;
    const mediaRowName = mediaRowNamesById[payload.mediaRowValue as string];

    return {
      headerName,
      displayValue,
      platformName,
      channelName,
      mediaRowName,
    };
  };

  const isSelectionIndeterminate = Boolean(selectedRowsIds.length);

  const hasSufficientViewportHeight = useHasSufficientViewportHeight();
  const columnsCount = isLoading
    ? DEFAULT_HEADER_CELLS_COUNT + 1
    : headers.length + 1;

  return (
    <>
      <InfinitelyPaginatedTable
        columnsCount={columnsCount}
        data={data}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        placeholderRowsCount={50}
        stickyHeader
        className="table-fixed"
        tableContainerProps={{
          id: 'data-table-container',
          className: cn(
            'min-h-0',
            hasSufficientViewportHeight ? 'shrink' : 'shrink-0',
          ),
        }}
      >
        <TableHead>
          <TableRow>
            <TableHeaderCell
              ref={setFirstCellRef}
              className="w-[52px]"
              skeletonVariant="checkbox"
            >
              {isLoading ? (
                <Skeleton variant="rounded" className="h-3.5 w-3.5" />
              ) : (
                <Checkbox
                  checked={
                    isSelectionIndeterminate
                      ? 'indeterminate'
                      : selection.isInverted
                  }
                  onCheckedChange={handleSelectAllRows}
                  aria-label={t('Select all rows')}
                  className="cursor-pointer"
                />
              )}
            </TableHeaderCell>
            {isLoading
              ? Array.from(
                  { length: DEFAULT_HEADER_CELLS_COUNT },
                  (_, index) => `header-skeleton-${index}`,
                ).map((skeletonId) => (
                  <TableHeadCellSkeleton key={skeletonId} />
                ))
              : headers.map((col, index) => {
                  const isWideColumn = [
                    'platform',
                    'media_row',
                    'channel',
                  ].includes(col.formatType);
                  const columnWidth = isWideColumn ? '270px' : '150px';

                  return (
                    <TableHeaderCell
                      key={`${col.headerName}-${col.columnPosition}`}
                      className={cn(
                        'break-words',
                        !isFirstCellOverflow &&
                          (columnWidth === '270px' ? 'w-[270px]' : 'w-[150px]'),
                      )}
                    >
                      {col.label}
                      {channelColumnIndex === index && (
                        <RichTooltip
                          description={t(
                            'Match each channel to its corresponding platform within the selected campaign.',
                          )}
                        >
                          <Information className="fill-icon-secondary" />
                        </RichTooltip>
                      )}
                    </TableHeaderCell>
                  );
                })}
          </TableRow>
        </TableHead>
        <TableBody>
          {paddingTop > 0 && (
            <tr>
              <td style={{ height: `${paddingTop}px` }} />
            </tr>
          )}
          {virtualRows.map((virtualRow) => {
            const rowIndex = virtualRow.index;
            const row = rows[rowIndex];
            const platformCell = row.validatedCells[platformColumnIndex];
            const channelCell = row.validatedCells[channelColumnIndex];
            const mediaRowCell = row.validatedCells[mediaRowColumnIndex];

            const isCustomBudgetingStructure =
              !dataFilePreview.hasDefaultBudgetingStructure;

            return (
              <ValidatedRow
                key={row.id}
                row={row}
                isSelected={
                  selection.isInverted
                    ? selection.selectedRows[row.id] !== false
                    : selection.selectedRows[row.id]
                }
                handleSelectRow={handleSelectRow(row.id, rowIndex)}
              >
                {({ cell, colIndex }) => {
                  const header = headers[colIndex];
                  const isValid = !cell.error && !cell.isEdited;

                  return (
                    <TableBodyValidatedCell
                      key={`${header.headerName}-${header.columnPosition}`}
                      formatType={header.formatType}
                      cell={cell}
                      platformCell={platformCell}
                      channelCell={channelCell}
                      mediaRowCell={mediaRowCell}
                      platformsByName={platformsByName}
                      channelsByName={channelsByName}
                      mediaRowNamesById={mediaRowNamesById}
                      isDisabled={isValid || isCustomBudgetingStructure}
                      handleEditField={handleEditField(
                        row.id,
                        rowIndex,
                        colIndex,
                      )}
                      granularityLevel={dataFilePreview.granularityLevel}
                    />
                  );
                }}
              </ValidatedRow>
            );
          })}
          {paddingBottom > 0 && (
            <tr>
              <td style={{ height: `${paddingBottom * 2}px` }} />
            </tr>
          )}
        </TableBody>
      </InfinitelyPaginatedTable>

      {channelToAssign && (
        <AssignChannelDialog
          channelName={channelsByName[channelToAssign.value]?.label ?? ''}
          platformName={
            platformsByName[channelToAssign.platformValue]?.label ?? ''
          }
          onClose={() => setChannelToAssign(null)}
          onBulkConfirm={() => handleBulkEditChannel(channelToAssign)}
          onBulkReject={() => handleEditChannel(channelToAssign)}
        />
      )}

      {bulkEditPayload && (
        <BulkEditConfirmationDialog
          {...getEntitiesDisplayNames(bulkEditPayload)}
          onBulkConfirm={() => editField({ ...bulkEditPayload, isBulk: true })}
          onBulkReject={() => editField(bulkEditPayload)}
          onClose={() => setBulkEditPayload(null)}
        />
      )}
    </>
  );
};
